import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertCircle, Phone, Package, ArrowLeft, Search, Building2, DollarSign, Users, MapPin } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useServices, useServiceOptions } from "@/hooks/useServices";
import { useCreateBookingOption } from "@/hooks/useBookings";
import type { CreateBookingOptionRequest } from "@/types/activities";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";

interface CreateBookingOptionModalProps {
  open: boolean;
  onClose: () => void;
  bookingId: string;
  onSuccess?: () => void;
}

type Step = 'service-selection' | 'option-selection' | 'confirmation';

interface SelectedServiceOption {
  serviceOptionId: string;
  serviceName: string;
  serviceOptionName: string;
  vendorId: string;
  vendorName: string;
  basePrice?: number;
}

export default function CreateBookingOptionModal({
  open,
  onClose,
  bookingId,
  onSuccess
}: CreateBookingOptionModalProps) {
  const [step, setStep] = useState<Step>('service-selection');
  const [selectedServiceId, setSelectedServiceId] = useState<string>("");
  const [selectedOption, setSelectedOption] = useState<SelectedServiceOption | null>(null);
  const [contactNotes, setContactNotes] = useState("");
  const [searchTerm, setSearchTerm] = useState("");

  const { toast } = useToast();
  const { data: services = [], loading: servicesLoading } = useServices();
  const { data: serviceOptions = [], loading: optionsLoading } = useServiceOptions(selectedServiceId);
  const createBookingOption = useCreateBookingOption();

  // Filter services by search term
  const filteredServices = services.filter(service =>
    service.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    service.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleServiceSelect = (serviceId: string) => {
    setSelectedServiceId(serviceId);
    setStep('option-selection');
  };

  const handleOptionSelect = (option: any) => {
    const service = services.find(s => s.id === selectedServiceId);

    setSelectedOption({
      serviceOptionId: option.id,
      serviceName: service?.name || 'Service',
      serviceOptionName: option.name || 'Option',
      vendorId: service?.vendorId || '',
      vendorName: service?.vendorName || 'Unknown Vendor',
      basePrice: option.basePrice
    });
    setStep('confirmation');
  };

  const handleSubmit = async () => {
    if (!selectedOption) return;

    try {
      await createBookingOption.mutateAsync({
        bookingId,
        data: {
          serviceOptionId: selectedOption.serviceOptionId,
          contactNotes: contactNotes
        }
      });

      toast({
        title: "Booking option created",
        description: `Option created with status "Exploring" for ${selectedOption.vendorName}`
      });

      handleClose();
      onSuccess?.();
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Failed to create booking option. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleClose = () => {
    setStep('service-selection');
    setSelectedServiceId("");
    setSelectedOption(null);
    setContactNotes("");
    setSearchTerm("");
    onClose();
  };

  const handleBack = () => {
    if (step === 'option-selection') {
      setStep('service-selection');
      setSelectedServiceId("");
    } else if (step === 'confirmation') {
      setStep('option-selection');
      setSelectedOption(null);
    }
  };

  const formatPrice = (price?: number) => {
    if (!price) return "Contact for pricing";
    return new Intl.NumberFormat('en-AE', {
      style: 'currency',
      currency: 'AED'
    }).format(price);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <div className="flex items-center gap-2">
            {step !== 'service-selection' && (
              <Button variant="ghost" size="sm" onClick={handleBack}>
                <ArrowLeft className="w-4 h-4" />
              </Button>
            )}
            <div className="flex-1">
              <DialogTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Add Alternative Booking Option
              </DialogTitle>
              <DialogDescription>
                {step === 'service-selection' && "Browse marketplace and select a service"}
                {step === 'option-selection' && "Select a service option"}
                {step === 'confirmation' && "Confirm and add contact notes"}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {/* Important Notice */}
        <Alert className="bg-blue-50 border-blue-200">
          <AlertCircle className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800 text-sm">
            <strong>Note:</strong> This will create a new booking option with "Exploring" status. You can only create this if all current options are rejected or unavailable.
          </AlertDescription>
        </Alert>

        {/* Step Content */}
        <div className="flex-1 overflow-hidden">
          {/* Step 1: Service Selection */}
          {step === 'service-selection' && (
            <div className="space-y-4 h-full flex flex-col">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search services..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>

              <ScrollArea className="flex-1">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pr-4">
                  {servicesLoading ? (
                    Array.from({ length: 6 }).map((_, i) => (
                      <Card key={i} className="h-32 animate-pulse bg-muted" />
                    ))
                  ) : filteredServices.length === 0 ? (
                    <div className="col-span-2 text-center py-8 text-muted-foreground">
                      No services found matching your search.
                    </div>
                  ) : (
                    filteredServices.map((service) => (
                      <Card
                        key={service.id}
                        className="cursor-pointer hover:shadow-md hover:border-primary transition-all"
                        onClick={() => handleServiceSelect(service.id)}
                      >
                        <CardHeader className="pb-3">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <CardTitle className="text-sm line-clamp-1">{service.name}</CardTitle>
                              <div className="flex items-center gap-2 mt-1">
                                <Building2 className="h-3 w-3 text-muted-foreground" />
                                <span className="text-xs text-muted-foreground">{service.vendorName || 'Unknown Vendor'}</span>
                              </div>
                            </div>
                            {service.isActive && (
                              <Badge variant="outline" className="text-xs bg-green-100 text-green-800">Active</Badge>
                            )}
                          </div>
                        </CardHeader>
                        <CardContent className="pt-0">
                          <p className="text-xs text-muted-foreground line-clamp-2">
                            {service.description || 'No description available'}
                          </p>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Step 2: Service Option Selection */}
          {step === 'option-selection' && (
            <div className="space-y-4 h-full flex flex-col">
              <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                <Package className="h-4 w-4" />
                <div>
                  <p className="text-sm font-medium">
                    {services.find(s => s.id === selectedServiceId)?.name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Select a service option below
                  </p>
                </div>
              </div>

              <ScrollArea className="flex-1">
                <div className="space-y-3 pr-4">
                  {optionsLoading ? (
                    Array.from({ length: 4 }).map((_, i) => (
                      <Card key={i} className="h-24 animate-pulse bg-muted" />
                    ))
                  ) : serviceOptions.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      No options available for this service.
                    </div>
                  ) : (
                    serviceOptions.map((option) => (
                      <Card
                        key={option.id}
                        className="cursor-pointer hover:shadow-md hover:border-primary transition-all"
                        onClick={() => handleOptionSelect(option)}
                      >
                        <CardContent className="p-4">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="font-medium text-sm">{option.name}</h4>
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                {option.description || 'No description'}
                              </p>
                              <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                                {option.capacity && (
                                  <div className="flex items-center gap-1">
                                    <Users className="h-3 w-3" />
                                    <span>Up to {option.capacity} people</span>
                                  </div>
                                )}
                                {option.location && (
                                  <div className="flex items-center gap-1">
                                    <MapPin className="h-3 w-3" />
                                    <span>{option.location}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                            <div className="ml-4 text-right">
                              <div className="flex items-center gap-1 text-primary font-semibold">
                                <span>{formatPrice(option.basePrice)}</span>
                              </div>
                              {option.isActive && (
                                <Badge variant="outline" className="text-xs mt-1">Available</Badge>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>
          )}

          {/* Step 3: Confirmation */}
          {step === 'confirmation' && selectedOption && (
            <div className="space-y-4 h-full flex flex-col">
              <Card className="bg-green-50 border-green-200">
                <CardHeader className="pb-3">
                  <CardTitle className="text-sm text-green-800">Selected Option</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <span className="font-medium text-green-900">Service:</span>
                      <p className="text-green-800">{selectedOption.serviceName}</p>
                    </div>
                    <div>
                      <span className="font-medium text-green-900">Option:</span>
                      <p className="text-green-800">{selectedOption.serviceOptionName}</p>
                    </div>
                    <div>
                      <span className="font-medium text-green-900">Vendor:</span>
                      <p className="text-green-800">{selectedOption.vendorName}</p>
                    </div>
                    <div>
                      <span className="font-medium text-green-900">Price:</span>
                      <p className="text-green-800">{formatPrice(selectedOption.basePrice)}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Separator />

              <div className="space-y-2 flex-1">
                <Label htmlFor="contactNotes">Contact Notes (Optional)</Label>
                <Textarea
                  id="contactNotes"
                  value={contactNotes}
                  onChange={(e) => setContactNotes(e.target.value)}
                  placeholder="Enter notes for vendor contact (e.g., special requirements, preferred dates, capacity needs...)"
                  rows={5}
                  className="resize-none"
                />
                <p className="text-xs text-muted-foreground">
                  These notes will be used when contacting the vendor for this booking option.
                </p>
              </div>

              <Alert>
                <Phone className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  <strong>Next Step:</strong> This option will be created with "Exploring" status. You can then update it with the vendor's response.
                </AlertDescription>
              </Alert>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={handleClose}>
            Cancel
          </Button>
          {step === 'confirmation' && (
            <Button
              onClick={handleSubmit}
              disabled={createBookingOption.isPending}
              className="flex items-center gap-2"
            >
              {createBookingOption.isPending ? (
                "Creating..."
              ) : (
                <>
                  <Phone className="w-4 h-4" />
                  Create Option
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
